var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack, Typography, useTheme } from "@mui/material";
import useIosContext from "@nitra/nkmb-card-components/contexts/IosContextProvider";
import { EmblemMaribor } from "@nitra/nkmb-theme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useIsMobileContext, useTimeManagementContext } from "../contexts";
import { areDatesEqual } from "../contexts/timeContextHelpers/AreDatesEqual";
import { getCalendar } from "../contexts/timeContextHelpers/GetCalendar";
import { getMonthName } from "../contexts/timeContextHelpers/GetMonthName";
import { CardWithShadow } from "./components";
/**
 * Calendar card for a single month without controls, used for the year overview
 * @param props
 * @constructor
 */
export function CalendarCardWithoutLogic(props) {
    var month = props.month, year = props.year;
    var i18n = useTranslation().i18n;
    var currentLang = i18n.language;
    var matches = useTimeManagementContext().matches;
    return (_jsx(CalendarCard, { monthNameLong: getMonthName(month, true, currentLang), weeks: getCalendar(month, year), matches: matches, prominentMonth: month }));
}
/**
 * Calendar card for a single month with controls, used for landing page
 */
export function CalendarCardWithLogic() {
    var _a = useTimeManagementContext(), monthNameLong = _a.monthNameLong, weeks = _a.weeks, matches = _a.matches, month = _a.month, handleGoIntoPast = _a.handleGoIntoPast, handleGoIntoFuture = _a.handleGoIntoFuture, canGoIntoPast = _a.canGoIntoPast, canGoIntoFuture = _a.canGoIntoFuture;
    return (_jsx(CalendarCard, { monthNameLong: monthNameLong, weeks: weeks, matches: matches, prominentMonth: month, handleGoIntoPast: handleGoIntoPast, handleGoIntoFuture: handleGoIntoFuture, canGoIntoPast: canGoIntoPast, canGoIntoFuture: canGoIntoFuture }));
}
export function CalendarCard(props) {
    var _a;
    var monthNameLong = props.monthNameLong, weeks = props.weeks, matches = props.matches, handleGoIntoPast = props.handleGoIntoPast, handleGoIntoFuture = props.handleGoIntoFuture, prominentMonth = props.prominentMonth, canGoIntoPast = props.canGoIntoPast, canGoIntoFuture = props.canGoIntoFuture;
    var theme = useTheme();
    var isMobile = useIsMobileContext();
    var isIos = useIosContext().isIos;
    return (_jsx(CardWithShadow, __assign({ fixedWidth: isMobile ? undefined : 288, disableOnHoverActions: true, sx: {
            display: "flex",
            flexDirection: "column",
        } }, { children: _jsxs(Stack, __assign({ sx: { mx: 2, my: 4, } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", justifyContent: "space-between" }, { children: [_jsx(Typography, __assign({ variant: "h4", sx: (_a = {},
                                _a[theme.breakpoints.down("sm")] = isIos && {
                                    '@media (-webkit-min-device-pixel-ratio: 2)': {
                                        fontWeight: 500, // Adjust font weight for iPhones
                                    },
                                },
                                _a) }, { children: monthNameLong })), handleGoIntoPast &&
                            _jsx(IconButton, __assign({ onClick: handleGoIntoPast, sx: { ml: "auto" }, disabled: canGoIntoPast === false }, { children: _jsx(ChevronLeft, { style: { color: theme.palette.text.primary } }) })), handleGoIntoFuture &&
                            _jsx(IconButton, __assign({ onClick: handleGoIntoFuture, disabled: canGoIntoFuture === false }, { children: _jsx(ChevronRight, { style: { color: theme.palette.text.primary } }) }))] })), _jsx(Stack, { children: weeks.map(function (week, index) {
                        return _jsxs(Box, { children: [_jsx(Stack, __assign({ direction: "row", alignItems: "center", justifyContent: "space-between" }, { children: week.map(function (day, index) {
                                        return _jsx(DayBox, { day: day, matches: matches, prominentMonth: prominentMonth }, index);
                                    }) }), index), index !== weeks.length - 1 && _jsx(Divider, {})] }, index);
                    }) })] })) })));
}
/**
 * Single day box in the calendar card
 * @param props
 * @param props.day - Date object of the day
 * @param props.prominentMonth - Month that is currently being displayed
 * @param props.matches - List of matches for season
 * @constructor
 */
function DayBox(props) {
    var day = props.day, matches = props.matches, prominentMonth = props.prominentMonth;
    var navigate = useNavigate();
    var _a = useState(undefined), matchingMatch = _a[0], setMatchingMatch = _a[1];
    useEffect(function () {
        var match = matches.find(function (match) { return areDatesEqual(day.date, match.date); });
        setMatchingMatch(match);
    }, [matches, day]);
    function handleClick() {
        if (matchingMatch) {
            var buyTicketLink = matchingMatch.buyTicketLink, reportId = matchingMatch.reportId, liveTickerKey = matchingMatch.liveTickerKey;
            if (reportId) {
                navigate("/news/" + reportId);
            }
            else if (liveTickerKey) {
                navigate("/live-ticker/" + liveTickerKey);
            }
            else if (buyTicketLink) {
                window.open(buyTicketLink, "_blank");
            }
        }
    }
    return (_jsxs(Box, __assign({ onClick: handleClick, sx: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "42px",
            height: "42px",
            pt: "4px",
            cursor: matchingMatch ? "pointer" : "default",
        } }, { children: [_jsx(Typography, __assign({ variant: "bodyM", sx: { opacity: day.date.getMonth() === prominentMonth ? 1 : 0.3 } }, { children: day.date.getDate() })), !!matchingMatch && _jsx(EmblemMaribor, { style: { width: "24px", height: "24px" } })] })));
}
